<template>
    <main class="account-page flex-1 min-h-0 overflow-hidden bg-stage-background">
        <div class="account-page-shell mx-auto flex h-full min-h-0 w-full max-w-7xl flex-col px-4 py-8 sm:px-6 lg:px-8">
            <header class="flex-shrink-0">
                <div class="flex items-center justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="account-main-title stage-page-title">
                            {{ $t('Account settings') }}
                        </h1>
                    </div>
                </div>
            </header>
            <section class="mt-6 min-h-0 flex-1">
                <app-panel
                    title="Accountdetails"
                    panel-class="account-settings-panel flex h-full min-h-0 flex-col overflow-hidden"
                    header-class="account-settings-panel-header"
                    body-class="min-h-0 flex-1 overflow-y-auto p-0"
                >
                    <template #header-actions>
                        <button
                            :aria-busy="loading.account ? 'true' : 'false'"
                            :class="{ 'cursor-not-allowed': loading.account }"
                            class="account-settings-save-button btn-stage-primary h-full border-0"
                            type="button"
                            @click="saveAccountSettings"
                        >
                            {{ $t('Save') }}
                        </button>
                    </template>
                    <div class="account-settings-sections">
                <div class="account-card">
                        <div class="account-form">
                            <loading :status="loading.details"/>
                            <div class="account-card-body">
                                <div>
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Personal information') }}</h3>
                                </div>
                                <div class="mt-5">
                                    <div class="grid grid-cols-3 gap-6">
                                        <div class="col-span-3 md:col-span-1">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="salutation">{{ $t('Salutation') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <select
                                                    id="salutation"
                                                    v-model="user.salutation"
                                                    class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                >
                                                    <option :value="null">{{ $t('Select salutation') }}</option>
                                                    <option value="mr">{{ $t('Mr') }}</option>
                                                    <option value="ms">{{ $t('Ms') }}</option>
                                                    <option value="diverse">{{ $t('Diverse') }}</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div class="col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="name">{{ $t('Name') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input id="name" v-model="user.name" :placeholder="$t('Name')" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5" maxlength="255" required>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6 grid grid-cols-3 gap-6">
                                        <div class="col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="email">{{ $t('Email') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input id="email" v-model="user.email" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5" maxlength="255" placeholder="email@email.com" required type="email">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6 grid grid-cols-1 md:grid-cols-10 gap-6">
                                        <div class="md:col-span-5">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="street">{{ $t('Street') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input id="street" v-model="user.street" :placeholder="$t('Street')" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5" maxlength="255">
                                            </div>
                                        </div>
                                        <div class="md:col-span-2">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="house_number">{{ $t('House number') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input id="house_number" v-model="user.house_number" placeholder="Nr" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5" maxlength="32">
                                            </div>
                                        </div>
                                        <div class="md:col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="postal_code">{{ $t('Postal code') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input id="postal_code" v-model="user.postal_code" :placeholder="$t('Postal code')" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5" maxlength="20" pattern="[A-Za-z0-9][A-Za-z0-9 \-]{1,19}" title="Bitte geben Sie eine gültige Postleitzahl ein.">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6 grid grid-cols-1 md:grid-cols-10 gap-6">
                                        <div class="md:col-span-7">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="city">{{ $t('City') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input id="city" v-model="user.city" :placeholder="$t('City')" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5" maxlength="120">
                                            </div>
                                        </div>
                                        <div class="md:col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="country">{{ $t('Country') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input id="country" v-model="user.country" :placeholder="$t('Country')" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5" maxlength="120">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6 grid grid-cols-1 gap-6">
                                        <div>
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="phone">{{ $t('Phone number') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="phone"
                                                    v-model="user.phone"
                                                    :placeholder="$t('Phone number')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    inputmode="tel"
                                                    maxlength="50"
                                                    pattern="\+?[0-9][0-9 ()/.\-]{2,49}"
                                                    title="Bitte geben Sie eine gültige Telefonnummer ein."
                                                    type="tel"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6">
                                        <label class="block text-sm leading-5 font-medium text-gray-700">Profilbild</label>
                                        <div class="mt-2 flex items-center select-none">
                                            <div class="avatar-editor relative inline-block h-12 w-12 overflow-hidden bg-gray-100">
                                                <button
                                                    v-show="user.avatar !== 'gravatar'"
                                                    class="avatar-editor-remove absolute top-0 right-0 items-center p-0.5 rounded-full leading-4 bg-red-600 text-white cursor-pointer"
                                                    type="button"
                                                    @click="removeAvatar"
                                                >
                                                    <svg-vue class="h-4 w-4 p-px" icon="font-awesome.times-light"></svg-vue>
                                                </button>
                                                <img
                                                    :src="user.avatar === 'gravatar' ? user.gravatar : user.avatar_preview"
                                                    alt="User avatar"
                                                    class="h-full w-full text-gray-300 rounded-full"
                                                />
                                            </div>
                                            <span class="ml-5">
                                                <input ref="changeAvatar" accept=".png,.jpg,.jpeg" hidden type="file" @change="changeAvatar($event)">
                                                <button
                                                    class="py-2 px-3 border border-bordergray-100 rounded text-sm leading-4 font-medium text-gray-700 bg-white hover:text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-0 active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
                                                    type="button"
                                                    @click="selectAvatar"
                                                >
                                                    Profilbild ändern
                                                </button>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="mt-6 border-t border-ultralight pt-5">
                                        <h4 class="text-sm font-semibold text-gray-900">Social Media</h4>
                                        <div class="mt-3 grid grid-cols-1 gap-6 md:grid-cols-2">
                                            <div>
                                                <label class="block text-sm font-medium leading-5 text-gray-700" for="linkedin_url">LinkedIn</label>
                                                <div class="mt-1 relative rounded shadow-sm">
                                                    <input id="linkedin_url" v-model.trim="user.linkedin_url" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5" maxlength="255" placeholder="https://www.linkedin.com/in/..." type="url">
                                                </div>
                                            </div>
                                            <div>
                                                <label class="block text-sm font-medium leading-5 text-gray-700" for="xing_url">XING</label>
                                                <div class="mt-1 relative rounded shadow-sm">
                                                    <input id="xing_url" v-model.trim="user.xing_url" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5" maxlength="255" placeholder="https://www.xing.com/profile/..." type="url">
                                                </div>
                                            </div>
                                            <div>
                                                <label class="block text-sm font-medium leading-5 text-gray-700" for="instagram_url">Instagram</label>
                                                <div class="mt-1 relative rounded shadow-sm">
                                                    <input id="instagram_url" v-model.trim="user.instagram_url" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5" maxlength="255" placeholder="https://www.instagram.com/..." type="url">
                                                </div>
                                            </div>
                                            <div>
                                                <label class="block text-sm font-medium leading-5 text-gray-700" for="facebook_url">Facebook</label>
                                                <div class="mt-1 relative rounded shadow-sm">
                                                    <input id="facebook_url" v-model.trim="user.facebook_url" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5" maxlength="255" placeholder="https://www.facebook.com/..." type="url">
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                <div v-if="user.is_admin" class="account-card">
                        <div class="account-form">
                            <loading :status="loading.relationships"/>
                            <div class="account-card-body">
                                <div>
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">Beziehungen</h3>
                                    <p class="mt-1 text-sm leading-5 text-gray-500">
                                        Legt fest, welche Benutzergruppen deinen Account und deine Kontaktdaten im Bereich Beziehungen sehen dürfen.
                                    </p>
                                </div>
                                <div class="mt-5">
                                    <div class="account-relationship-selection overflow-hidden rounded border border-gray-200 bg-white">
                                        <label class="flex cursor-pointer items-center border-b border-gray-200 px-3 py-2">
                                            <input
                                                v-model="user.relationships_contact_visible_to_customers"
                                                class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                                type="checkbox"
                                            >
                                            <span class="text-sm text-gray-700">Kunden</span>
                                        </label>
                                        <label class="flex cursor-pointer items-center border-b border-gray-200 px-3 py-2">
                                            <input
                                                v-model="user.relationships_contact_visible_to_employees"
                                                class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                                type="checkbox"
                                            >
                                            <span class="text-sm text-gray-700">Mitarbeiter</span>
                                        </label>
                                        <label class="flex cursor-pointer items-center px-3 py-2">
                                            <input
                                                v-model="user.relationships_contact_visible_to_service_providers"
                                                class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                                type="checkbox"
                                            >
                                            <span class="text-sm text-gray-700">Mitarbeitende Dienstleister</span>
                                        </label>
                                    </div>
                                    <p class="mt-2 text-xs leading-5 text-gray-500">
                                        Die Checkbox erteilt nur die grundsätzliche Freigabe. Sichtbar wird dein Account für die ausgewählte Benutzergruppe erst, wenn zusätzlich eine direkte Verknüpfung im System besteht, zum Beispiel über ein gemeinsames Unternehmen oder eine aktive Ticketteilnahme.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                <div v-if="notificationSettings.length > 0" class="account-card">
                        <div class="account-form">
                            <loading :status="loading.notificationSettings"/>
                            <div class="account-card-body">
                                <div>
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Notification settings') }}</h3>
                                    <p class="mt-1 text-sm leading-5 text-gray-500">
                                        Systembenachrichtigungen, die per E-Mail versendet werden.
                                    </p>
                                </div>
                                <div class="mt-5">
                                    <div class="account-notification-list overflow-hidden rounded border border-gray-200 bg-white">
                                        <label
                                            v-for="item in notificationSettings"
                                            :key="'notification-setting-' + item.key"
                                            :for="'notification-setting-checkbox-' + item.key"
                                            class="grid cursor-pointer grid-cols-12 items-center gap-3 border-b border-gray-200 px-3 py-2 last:border-b-0"
                                        >
                                            <div class="col-span-3 min-w-0 truncate text-sm font-normal text-gray-800">
                                                {{ translateTemplateLabel(item.module_translation_key, item.module) }}
                                            </div>
                                            <div class="col-span-8 min-w-0 truncate text-sm font-normal text-gray-500">
                                                {{ translateTemplateLabel(item.name_translation_key, item.name) }}
                                            </div>
                                            <input
                                                :id="'notification-setting-checkbox-' + item.key"
                                                v-model="item.enabled"
                                                class="form-checkbox col-span-1 h-4 w-4 justify-self-end text-gray-700"
                                                type="checkbox"
                                            >
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                <div class="account-card">
                        <div class="account-form">
                            <loading :status="loading.details"/>
                            <div class="account-card-body">
                                <div>
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('AI settings') }}</h3>
                                    <p class="mt-1 text-sm leading-5 text-gray-500">
                                        Steuerung KI-gestützter Funktionen für dieses Benutzerkonto.
                                    </p>
                                </div>
                                <div class="mt-5">
                                    <div class="flex items-center">
                                        <input-switch
                                            id="account_ai_enabled"
                                            v-model="user.ai_enabled"
                                        ></input-switch>
                                        <span class="ml-3 text-sm text-gray-700">{{ $t('Enable AI functions') }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                <div v-if="showAuthenticationSettingsCard" class="account-card">
                        <div class="account-form">
                            <loading :status="loading.authenticationMethod"/>
                            <div class="account-card-body">
                                <div>
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Authentication method') }}</h3>
                                    <p class="mt-1 text-sm leading-5 text-gray-500">
                                        {{ authenticationMethodDescription }}
                                    </p>
                                </div>
                                <div class="mt-5">
                                    <div v-if="user.is_primary_admin" class="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                        Für das primäre Administratorkonto sind Einmalcode-Anmeldung und Passwort-Anmeldung verfügbar. Die Passwort-Anmeldung bleibt dauerhaft aktiv.
                                    </div>
                                    <div v-else class="space-y-4">
                                        <div v-if="user.can_manage_auth_login_method && user.password_login_available">
                                            <div class="mt-1 flex rounded">
                                                <input-switch
                                                    id="password_login_enabled"
                                                    v-model="user.password_login_enabled"
                                                    :disabled-label="$t('Enable password login')"
                                                    :enabled-label="$t('Enable password login')"
                                                ></input-switch>
                                            </div>
                                        </div>
                                        <div v-else class="rounded border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                                            Die zusätzliche Passwort-Anmeldung kann nur aktiviert werden, wenn dies in den Authentifizierungseinstellungen freigegeben ist.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                <div v-if="showChangePasswordCard" class="account-card">
                        <form class="account-form" @submit.prevent="changePassword">
                            <loading :status="loading.password"/>
                            <div class="account-card-body">
                                <div>
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Change password') }}</h3>
                                    <p class="mt-1 text-sm leading-5 text-gray-500">
                                        Festlegung eines neuen Passworts für die nächste Anmeldung.
                                    </p>
                                </div>
                                <div class="mt-5">
                                    <div class="grid grid-cols-3 gap-6">
                                        <div class="col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="current_password">{{ $t('Current password') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="current_password"
                                                    v-model="user.current_password"
                                                    :placeholder="$t('Current password')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    type="password"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6 grid grid-cols-3 gap-6">
                                        <div class="col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="password">{{ $t('New password') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="password"
                                                    v-model="user.password"
                                                    :placeholder="$t('New password')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    type="password"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6 grid grid-cols-3 gap-6">
                                        <div class="col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="password_confirmation">{{ $t('Confirm password') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="password_confirmation"
                                                    v-model="user.password_confirmation"
                                                    :placeholder="$t('Confirm password')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    type="password"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="account-password-action flex justify-end px-7 pb-7">
                                <button class="btn btn-white whitespace-nowrap" type="submit">
                                    {{ $t('Change password') }}
                                </button>
                            </div>
                        </form>
                    </div>
                    </div>
                </app-panel>
            </section>
        </div>
    </main>
</template>

<script>
export default {
    name: "Account",
    metaInfo() {
        return {
            title: this.$i18n.t('Account')
        }
    },
    mounted() {
        this.getUser();
        this.getNotificationSettings();
    },
    data() {
        return {
            loading: {
                account: false,
                details: true,
                password: false,
                authenticationMethod: false,
                notificationSettings: false,
                relationships: false,
            },
            notificationSettings: [],
            user: {
                name: null,
                avatar: null,
                gravatar: null,
                avatar_preview: null,
                email: null,
                salutation: null,
                country: null,
                city: null,
                postal_code: null,
                street: null,
                house_number: null,
                phone: null,
                linkedin_url: null,
                xing_url: null,
                instagram_url: null,
                facebook_url: null,
                current_password: null,
                password: null,
                password_confirmation: null,
                auth_login_method: 'one_time_code',
                effective_auth_login_method: 'one_time_code',
                can_manage_auth_login_method: false,
                password_login_available: false,
                password_login_enabled: false,
                is_primary_admin: false,
                is_admin: false,
                ai_enabled: true,
                relationships_contact_visible_to_customers: false,
                relationships_contact_visible_to_employees: false,
                relationships_contact_visible_to_service_providers: false,
            },
        }
    },
    computed: {
        showAuthenticationSettingsCard() {
            return this.user.is_primary_admin || (this.user.can_manage_auth_login_method && this.user.password_login_available);
        },
        showChangePasswordCard() {
            return this.user.is_primary_admin || this.user.password_login_available;
        },
        authenticationMethodDescription() {
            if (this.user.is_primary_admin) {
                return 'Für das primäre Administratorkonto ist die Passwort-Anmeldung dauerhaft aktiv, damit der Zugriff unabhängig von der E-Mail-Zustellung möglich bleibt.';
            }

            if (this.user.password_login_available) {
                return 'Standard-Anmeldeverfahren ist ein Einmalcode per E-Mail. Die Passwort-Anmeldung kann zusätzlich aktiviert werden.';
            }

            return 'Standard-Anmeldeverfahren ist ein Einmalcode per E-Mail. Die Passwort-Anmeldung ist derzeit systemseitig deaktiviert.';
        }
    },
    methods: {
        getUser() {
            const self = this;
            self.loading.details = true;
            axios.get('api/auth/user').then(function (response) {
                self.loading.details = false;
                self.user.name = response.data.name;
                self.user.email = response.data.email;
                self.user.salutation = response.data.salutation;
                self.user.country = response.data.country;
                self.user.city = response.data.city;
                self.user.postal_code = response.data.postal_code;
                self.user.street = response.data.street;
                self.user.house_number = response.data.house_number;
                self.user.phone = response.data.phone || response.data.phone_mobile || response.data.phone_landline;
                self.user.linkedin_url = response.data.linkedin_url;
                self.user.xing_url = response.data.xing_url;
                self.user.instagram_url = response.data.instagram_url;
                self.user.facebook_url = response.data.facebook_url;
                self.user.avatar = response.data.avatar;
                self.user.gravatar = response.data.gravatar;
                self.user.avatar_preview = response.data.avatar;
                self.user.auth_login_method = response.data.auth_login_method || 'one_time_code';
                self.user.effective_auth_login_method = response.data.effective_auth_login_method || 'one_time_code';
                self.user.can_manage_auth_login_method = !!response.data.can_manage_auth_login_method;
                self.user.password_login_available = !!response.data.password_login_available;
                self.user.password_login_enabled = !!response.data.password_login_enabled;
                self.user.is_primary_admin = !!response.data.is_primary_admin;
                self.user.is_admin = !!(response.data.access && response.data.access.is_admin);
                self.user.ai_enabled = response.data.ai_enabled === null ? true : !!response.data.ai_enabled;
                self.user.relationships_contact_visible_to_customers = !!response.data.relationships_contact_visible_to_customers;
                self.user.relationships_contact_visible_to_employees = !!response.data.relationships_contact_visible_to_employees;
                self.user.relationships_contact_visible_to_service_providers = !!response.data.relationships_contact_visible_to_service_providers;
            }).catch(function () {
                self.loading.details = false;
            });
        },
        getNotificationSettings() {
            const self = this;
            self.loading.notificationSettings = true;
            axios.get('api/account/notification-settings').then(function (response) {
                self.notificationSettings = Array.isArray(response.data.items)
                    ? response.data.items.map(function (item) {
                        return {
                            key: item.key || '',
                            name: item.name || item.key || '',
                            name_translation_key: item.name_translation_key || '',
                            module: item.module || '',
                            module_translation_key: item.module_translation_key || '',
                            enabled: item.enabled !== false,
                        };
                    })
                    : [];
                self.loading.notificationSettings = false;
            }).catch(function () {
                self.loading.notificationSettings = false;
            });
        },
        saveNotificationSettings(shouldNotify = true) {
            const self = this;
            self.loading.notificationSettings = true;

            const preferences = {};
            self.notificationSettings.forEach(function (item) {
                if (!item || !item.key) {
                    return;
                }
                preferences[item.key] = !!item.enabled;
            });

            return axios.post('api/account/notification-settings', {
                preferences: preferences,
            }).then(function () {
                self.loading.notificationSettings = false;
                if (shouldNotify) {
                    self.$notify({
                        title: self.$i18n.t('Success').toString(),
                        text: self.$i18n.t('Notification settings updated successfully').toString(),
                        type: 'success'
                    });
                }
                return true;
            }).catch(function () {
                self.loading.notificationSettings = false;
                return false;
            });
        },
        saveRelationshipContactVisibility(shouldNotify = true) {
            const self = this;
            self.loading.relationships = true;

            return axios.post('api/account/relationship-contact-visibility', {
                relationships_contact_visible_to_customers: !!self.user.relationships_contact_visible_to_customers,
                relationships_contact_visible_to_employees: !!self.user.relationships_contact_visible_to_employees,
                relationships_contact_visible_to_service_providers: !!self.user.relationships_contact_visible_to_service_providers,
            }).then(function (response) {
                const savedUser = response.data && response.data.user ? response.data.user : {};
                self.user.relationships_contact_visible_to_customers =
                    !!savedUser.relationships_contact_visible_to_customers;
                self.user.relationships_contact_visible_to_employees =
                    !!savedUser.relationships_contact_visible_to_employees;
                self.user.relationships_contact_visible_to_service_providers =
                    !!savedUser.relationships_contact_visible_to_service_providers;
                self.loading.relationships = false;
                if (shouldNotify) {
                    self.$notify({
                        title: self.$i18n.t('Success').toString(),
                        text: self.$i18n.t('Settings updated successfully').toString(),
                        type: 'success'
                    });
                }
                return true;
            }).catch(function () {
                self.loading.relationships = false;
                return false;
            });
        },
        selectAvatar() {
            this.$refs.changeAvatar.click();
        },
        removeAvatar() {
            this.user.avatar = 'gravatar';
            this.user.avatar_preview = null;
            this.$refs.changeAvatar.value = '';
        },
        isSupportedAvatarFile(file) {
            return ['image/png', 'image/x-citrix-png', 'image/x-png', 'image/jpeg', 'image/x-citrix-jpeg'].includes(file.type);
        },
        replaceLaravelPlaceholders(text, params = {}) {
            let value = String(text || '');
            Object.keys(params).forEach((key) => {
                value = value.replace(new RegExp(':' + key + '\\b', 'g'), String(params[key] ?? ''));
            });
            return value;
        },
        translateLaravelMessage(key, params = {}) {
            return this.replaceLaravelPlaceholders(this.$i18n.t(key).toString(), params);
        },
        validateAvatarFile(file) {
            const self = this;

            return new Promise(function (resolve) {
                const objectUrl = URL.createObjectURL(file);
                const image = new Image();

                image.onload = function () {
                    const width = image.naturalWidth;
                    const height = image.naturalHeight;

                    URL.revokeObjectURL(objectUrl);

                    if (width !== height) {
                        resolve(self.translateLaravelMessage('The :attribute must be square', {
                            attribute: self.$i18n.t('Avatar').toString(),
                        }));
                        return;
                    }

                    if (width > 1000 || height > 1000) {
                        resolve(self.translateLaravelMessage('The :attribute may not be greater than :width x :height pixels', {
                            attribute: self.$i18n.t('Avatar').toString(),
                            width: 1000,
                            height: 1000,
                        }));
                        return;
                    }

                    resolve(null);
                };

                image.onerror = function () {
                    URL.revokeObjectURL(objectUrl);
                    resolve(self.translateLaravelMessage('The file has to be an image', {
                        attribute: self.$i18n.t('Avatar').toString(),
                    }));
                };

                image.src = objectUrl;
            });
        },
        changeAvatar(event) {
            const self = this;
            if (!event.target.files.length) {
                return;
            }

            const file = event.target.files[0];

            if (!self.isSupportedAvatarFile(file)) {
                self.$notify({
                    title: self.$i18n.t('Unsupported file type').toString(),
                    text: self.$i18n.t('Only image type files are accepted').toString(),
                    type: 'error'
                });
                event.target.value = '';
                return;
            }

            self.validateAvatarFile(file).then(function (errorText) {
                if (errorText) {
                    self.$notify({
                        title: self.$i18n.t('Error').toString(),
                        text: errorText,
                        type: 'error'
                    });
                    event.target.value = '';
                    return;
                }

                self.user.avatar = file;
                self.user.avatar_preview = URL.createObjectURL(file);
            });
        },
        editUser(shouldNotify = true) {
            const self = this;
            const formData = new FormData();
            self.loading.details = true;
            formData.append('name', self.user.name);
            formData.append('email', self.user.email);
            formData.append('salutation', self.user.salutation || '');
            formData.append('country', self.user.country || '');
            formData.append('city', self.user.city || '');
            formData.append('postal_code', self.user.postal_code || '');
            formData.append('street', self.user.street || '');
            formData.append('house_number', self.user.house_number || '');
            formData.append('phone', self.user.phone || '');
            formData.append('linkedin_url', self.user.linkedin_url || '');
            formData.append('xing_url', self.user.xing_url || '');
            formData.append('instagram_url', self.user.instagram_url || '');
            formData.append('facebook_url', self.user.facebook_url || '');
            formData.append('ai_enabled', self.user.ai_enabled ? '1' : '0');
            if (self.user.avatar === 'gravatar') {
                formData.append('gravatar', 'true');
            } else if (self.user.avatar instanceof File) {
                formData.append('avatar', self.user.avatar);
            }
            return axios.post('api/account/update', formData, {headers: {'Content-Type': 'multipart/form-data'}}).then(function (response) {
                self.loading.details = false;
                self.user.avatar = response.data.avatar;
                self.user.gravatar = response.data.gravatar;
                self.user.avatar_preview = response.data.avatar;
                self.$refs.changeAvatar.value = '';
                self.$store.commit('updateUser', response.data);
                if (shouldNotify) {
                    self.$notify({
                        title: self.$i18n.t('Success').toString(),
                        text: self.$i18n.t('Your account details have been updated').toString(),
                        type: 'success'
                    });
                }
                return true;
            }).catch(function () {
                self.loading.details = false;
                return false;
            });
        },
        changePassword() {
            const self = this;
            self.loading.password = true;
            axios.post('api/account/password', {
                current_password: self.user.current_password,
                password: self.user.password,
                password_confirmation: self.user.password_confirmation,
            }).then(function () {
                self.loading.password = false;
                self.user.current_password = null;
                self.user.password = null;
                self.user.password_confirmation = null;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Password changed successfully').toString(),
                    type: 'success'
                })
            }).catch(function () {
                self.loading.password = false;
            });
        },
        saveAuthenticationMethod(shouldNotify = true) {
            if (this.user.is_primary_admin || !this.user.can_manage_auth_login_method || !this.user.password_login_available) {
                return Promise.resolve(true);
            }

            const self = this;
            self.loading.authenticationMethod = true;
            return axios.post('api/account/authentication-method', {
                auth_login_method: self.user.password_login_enabled ? 'password' : 'one_time_code',
            }).then(function (response) {
                if (response.data && response.data.user) {
                    self.$store.commit('updateUser', response.data.user);
                    self.user.auth_login_method = response.data.user.auth_login_method || 'one_time_code';
                    self.user.effective_auth_login_method = response.data.user.effective_auth_login_method || 'one_time_code';
                    self.user.can_manage_auth_login_method = !!response.data.user.can_manage_auth_login_method;
                    self.user.password_login_available = !!response.data.user.password_login_available;
                    self.user.password_login_enabled = !!response.data.user.password_login_enabled;
                    self.user.is_primary_admin = !!response.data.user.is_primary_admin;
                }
                self.loading.authenticationMethod = false;
                if (shouldNotify) {
                    self.$notify({
                        title: self.$i18n.t('Success').toString(),
                        text: self.$i18n.t('Settings updated successfully').toString(),
                        type: 'success'
                    });
                }
                return true;
            }).catch(function () {
                self.loading.authenticationMethod = false;
                return false;
            });
        },
        async saveAccountSettings() {
            if (this.loading.account || this.loading.details) {
                return;
            }

            this.loading.account = true;

            try {
                let saved = await this.editUser(false);

                if (saved && this.user.is_admin) {
                    saved = await this.saveRelationshipContactVisibility(false);
                }

                if (saved && this.notificationSettings.length > 0) {
                    saved = await this.saveNotificationSettings(false);
                }

                if (
                    saved
                    && !this.user.is_primary_admin
                    && this.user.can_manage_auth_login_method
                    && this.user.password_login_available
                ) {
                    saved = await this.saveAuthenticationMethod(false);
                }

                if (saved) {
                    this.$notify({
                        title: this.$i18n.t('Success').toString(),
                        text: this.$i18n.t('Settings updated successfully').toString(),
                        type: 'success'
                    });
                }
            } finally {
                this.loading.account = false;
            }
        },
        translateTemplateLabel(key, fallback) {
            const normalizedFallback = typeof fallback === 'string' ? fallback : '';
            if (!key || typeof key !== 'string') {
                return normalizedFallback;
            }

            const translated = this.$t(key);
            if (typeof translated === 'string' && translated !== '' && translated !== key) {
                return translated;
            }

            return normalizedFallback;
        }
    }
}
</script>

<style lang="scss">
@reference "../../../../sass/tailwind-reference.css";

.account-page {
    .account-main-title {
        letter-spacing: 0;
    }

    .account-form label.block.text-sm.font-medium.leading-5.text-gray-700 {
        font-size: 0.75rem;
        letter-spacing: 0.02em;
        color: var(--ui-text-muted);
        margin-bottom: 0.5rem;
    }

    .account-form .form-input,
    .account-form .form-select,
    .account-form .form-textarea {
        width: 100%;
        border: 1px solid var(--ui-border-default);
        border-radius: 4px;
        box-shadow: none;
        background: #fff;
        color: var(--ui-text-heading);
        min-height: 2.5rem;
    }

    .account-form .form-input::placeholder,
    .account-form .form-textarea::placeholder {
        color: var(--ui-text-subtle);
    }

    .account-form .form-input:focus,
    .account-form .form-select:focus,
    .account-form .form-textarea:focus {
        border-color: var(--ui-border-strong);
        box-shadow: none;
    }

    .account-form .relative.rounded.shadow-sm,
    .account-form .flex.rounded.shadow-sm,
    .account-form .mt-1.relative.rounded.shadow-sm,
    .account-form .mt-1.flex.rounded.shadow-sm {
        box-shadow: none !important;
    }

    .account-form .account-input-prefix {
        border: 1px solid var(--ui-border-default);
        border-right-width: 0;
    }

    .account-form .account-input-connected {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }

    .account-form .form-select {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        padding-right: 2.25rem;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none'%3E%3Cpath d='M6 8l4 4 4-4' stroke='%23a3a3a3' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 0.65rem center;
        background-size: 1rem 1rem;
    }

    .account-card-body {
        padding: 1.75rem;
    }

    .account-settings-panel {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        overflow: hidden;

        > .app-panel-header,
        > .app-panel-footer {
            flex-shrink: 0;
        }

        > .app-panel-body {
            flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto;
        }

        .account-card {
            background: #fff;
            overflow: visible;
        }

        .account-card + .account-card {
            &::before {
                display: block;
                margin-inline: 1.75rem;
                border-top: 1px solid var(--border-ultralight);
                content: '';
            }
        }
    }

    .account-settings-panel-header {
        padding: 0;

        > .flex {
            align-items: stretch;
            gap: 0;
        }

        .stage-panel-header-label {
            display: flex;
            align-items: center;
            padding: 1rem 1.5rem;
        }

        .account-settings-save-button {
            min-height: 3.25rem;
            padding-right: 1.5rem;
            padding-left: 1.5rem;
            border-radius: 0;
        }
    }
}

.avatar-editor {
    .avatar-editor-remove {
        @apply hidden;
    }

    &:hover {
        .avatar-editor-remove {
            @apply inline-flex;
        }
    }
}
</style>
